import React, { memo, useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  X, 
  Printer,
  Languages,
  Mail,
  Phone,
  FileText,
  Loader2,
  IndianRupee
} from "lucide-react";
import { format, isValid } from "date-fns";
import { getClinicInfo, getDoctorInfo, getPatient, getSettings } from "@/lib/api";
import { toast } from "@/components/ui/use-toast";
import { PrescriptionContent } from "@/components/Print/PrescriptionContent";
import { InvoiceContent } from "@/components/Print/InvoiceContent";
import ErrorBoundary from '@/components/ErrorBoundary';
import {useAuth} from "@/context/AuthContext.tsx";
import {DateUtil} from "@/utils/dateUtil.ts";

// Shared translations for all print types
const printTranslations = {
  en: {
    // Common terms
    date: "Date",
    time: "Time",
    phone: "Phone",
    email: "Email",
    total: "Total",
    generated: "Generated on",
    information: "Information",
    details: "Details",

    
    // Medical terms
    morning: "Morning",
    afternoon: "Afternoon", 
    evening: "Evening",
    night: "Night",
    beforeFood: "Before Food",
    afterFood: "After Food",
    withFood: "With Food",
    emptyStomach: "Empty Stomach",
    days: "days",
    weeks: "weeks", 
    months: "months",
    asDirected: "As Directed",
    investigations: "Investigations:",
    nextVisit: "Next Visit:",
    advice: "Advice:",

    afterBreakfast: "After Breakfast",
    beforeBreakfast: "Before Breakfast",
    withBreakfast: "With Breakfast",

    afterLunch: "After Lunch",
    beforeLunch: "Before Lunch",
    withLunch: "With Lunch",

    afterDinner: "After Dinner",
    beforeDinner: "Before Dinner",
    withDinner: "With Dinner",

    bedtime: "At Bedtime",
    sos: "SOS",
    
    // Billing terms
    invoice: "Invoice",
    invoiceNumber: "Invoice Number",
    name: "Name",
    doctor: "Doctor",
    patient: "Patient",
    service: "Service",
    category: "Category",
    quantity: "Qty",
    unitPrice: "Unit Price",
    totalAmount: "Total Amount",
    amountPaid: "Amount Paid",
    balanceDue: "Balance Due",
    paymentMethod: "Payment Method",
    status: "Status",
    subtotal: "Subtotal",
    services: "Services",
    charges: "Charges",
  },
  hi: {
    // Common terms
    date: "दिनांक",
    time: "समय",
    phone: "फोन",
    email: "ईमेल",
    total: "कुल",
    generated: "उत्पन्न",
    information: "जानकारी",
    details: "विवरण",
    
    // Medical terms
    morning: "सुबह",
    afternoon: "दोपहर",
    evening: "शाम", 
    night: "रात",
    beforeFood: "खाना पहले",
    afterFood: "खाना बाद",
    withFood: "खाना के साथ",
    emptyStomach: "खाली पेट",
    days: "दिन",
    weeks: "सप्ताह",
    months: "महीने",
    asDirected: "निर्देशानुसार",
    investigations: "जांच चाहिये:",
    nextVisit: "अगली मुलाकात:",
    advice: "सलाह:",
    afterBreakfast: "नाश्ते के बाद",
    beforeBreakfast: "नाश्ते से पहले",
    withBreakfast: "नाश्ते के साथ",
    afterLunch: "दोपहर के भोजन के बाद",
    beforeLunch: "दोपहर के भोजन से पहले",
    withLunch: "दोपहर के भोजन के साथ",
    afterDinner: "रात के खाने के बाद",
    beforeDinner: "रात के खाने से पहले",
    withDinner: "रात के खाने के साथ",
    atBedtime: "सोने से पहले",
    sos: "ज़रूरत अनुसार",
    timing: "समय",
    notes: "निर्देश",
    patientName: "रोगी का नाम",
    doctorName: "डॉक्टर का नाम",
    ageGender: "आयु / लिंग",
    department: "विभाग",

    
    // Billing terms
    invoice: "बिल",
    invoiceNumber: "बिल संख्या",
    name: "नाम",
    doctor: "डॉक्टर",
    patient: "मरीज़",
    service: "सेवा",
    category: "श्रेणी",
    quantity: "मात्रा",
    unitPrice: "दर",
    totalAmount: "कुल राशि",
    amountPaid: "भुगतान",
    balanceDue: "बकाया",
    paymentMethod: "भुगतान विधि",
    status: "स्थिति",
    subtotal: "उप-योग",
    services: "सेवाएं",
    charges: "शुल्क",
  },
  mr: {
    // Common terms
    date: "दिनांक",
    time: "वेळ",
    phone: "फोन",
    email: "ईमेल",
    total: "एकूण",
    generated: "तयार केले",
    information: "माहिती",
    details: "तपशील",
    
    // Medical terms
    morning: "सकाळ", 
    afternoon: "दुपार",
    evening: "संध्याकाळ",
    night: "रात्री", 
    beforeFood: "जेवणापूर्वी",
    afterFood: "जेवणानंतर",
    withFood: "जेवणासह",
    emptyStomach: "रिकाम्या पोटी",
    days: "दिवस",
    weeks: "आठवडे",
    months: "महिने",
    asDirected: "निर्देशानुसार",
    investigations: "तपासणी आवश्यक:",
    nextVisit: "पुढची भेट:",
    advice: "सल्ला:",
    afterBreakfast: "नाश्त्यानंतर",
    beforeBreakfast: "नाश्त्यापूर्वी",
    withBreakfast: "नाश्त्यासह",

    afterLunch: "दुपारच्या जेवणानंतर",
    beforeLunch: "दुपारच्या जेवणापूर्वी",
    withLunch: "दुपारच्या जेवणासह",

    afterDinner: "रात्रीच्या जेवणानंतर",
    beforeDinner: "रात्रीच्या जेवणापूर्वी",
    withDinner: "रात्रीच्या जेवणासह",

    bedtime: "झोपण्यापूर्वी",
    sos: "गरजेनुसार",

    
    // Billing terms
    invoice: "बिल",
    invoiceNumber: "बिल क्रमांक",
    name: "नाव",
    doctor: "डॉक्टर",
    patient: "रुग्ण",
    service: "सेवा",
    category: "प्रकार",
    quantity: "संख्या",
    unitPrice: "दर",
    totalAmount: "एकूण रक्कम",
    amountPaid: "भरलेली रक्कम",
    balanceDue: "उरलेली रक्कम",
    paymentMethod: "पेमेंट पद्धत",
    status: "स्थिती",
    subtotal: "उप-एकूण",
    services: "सेवा",
    charges: "शुल्क",
  }
};

// Add proper types for the data structure
interface PrescriptionData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  date?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface InvoiceData {
  id?: string;
  invoice_number?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  date?: string;
  total_amount?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Medical data interfaces
interface ClinicInfo {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface DoctorInfo {
  name?: string;
  specialization?: string;
  registration_no?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface PatientInfo {
  name?: string;
  phone?: string;
  email?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface SettingsInfo {
  clinic?: ClinicInfo;
  profile?: DoctorInfo;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export type PrintType = 'prescription' | 'invoice';
export type PrintLanguage = 'en' | 'hi' | 'mr';

interface UnifiedPrintModalProps {
  open: boolean;
  onClose: () => void;
  type: PrintType;
  data: PrescriptionData | InvoiceData; // Use proper union type instead of any
  title?: string;
}

function UnifiedPrintModal({ 
  open, 
  onClose,
  type,
  data,
  title
}: Readonly<UnifiedPrintModalProps>) {

  const { currentUser } = useAuth();

  const [printLanguage, setPrintLanguage] = useState<PrintLanguage>('en');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [autoOpenPDF, setAutoOpenPDF] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSavingPDF, setIsSavingPDF] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Clinic and doctor info with proper types
  const [clinicInfo, setClinicInfo] = useState<ClinicInfo | null>(null);
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [settings, setSettings] = useState<SettingsInfo | null>(null);

  const t = printTranslations[printLanguage];

  // Load all necessary data
  useEffect(() => {
    if (!data) return;

    const loadData = async () => {
      try {



        // Load settings first (contains clinic and doctor info)
        if (data.doctor_id && data.clinic_id) {
          const settingsData = await getSettings(data.doctor_id, data.clinic_id);
          setSettings(settingsData);
          
          // Fallback to individual API calls if needed
          if (!settingsData?.profile?.name) {
            const doctor = await getDoctorInfo(data.doctor_id, data.clinic_id);
            setDoctorInfo(doctor);
          }
          if (!settingsData?.clinic?.name) {
            const clinic = await getClinicInfo(data.clinic_id, data.doctor_id);
            setClinicInfo(clinic);
          }
        }

        // Load patient info
        if (data.patient_id) {
          const patient = await getPatient(data.patient_id);
          setPatientInfo(patient);
          setPhone(patient.phone || '');
        }
      } catch {
        toast({
          title: "Data Loading Error",
          description: "Some information may not display correctly in the preview",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [data]);

  // Format date for display

  // Generate print title
  const generatePrintTitle = () => {
    const date = data?.date ? new Date(data.date).toLocaleDateString() : new Date().toLocaleDateString();
    const patientName = patientInfo?.name || data?.patient_name || 'Patient';
    const doctorName = settings?.profile?.name || doctorInfo?.name || 'Doctor';
    
    if (type === 'prescription') {
      return `Prescription - ${patientName} - ${date} - Dr. ${doctorName}`;
    } else {
      return `Invoice ${data?.invoice_number || data?.id} - ${patientName} - ${date}`;
    }
  };

  // Generate PDF filename
  const generateFilename = () => {
    const date = data?.date ? new Date(data.date) : new Date();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    
    const patientNameClean = (patientInfo?.name || data?.patient_name || 'Patient').replace(/[^a-zA-Z0-9]/g, '_');
    const doctorNameClean = (settings?.profile?.name || doctorInfo?.name || 'Doctor').replace(/[^a-zA-Z0-9]/g, '_');
    const clinicNameClean = (settings?.clinic?.name || clinicInfo?.name || 'Clinic').replace(/[^a-zA-Z0-9]/g, '_');
    
    if (type === 'prescription') {
      return `Prescription_${patientNameClean}_${dateStr}_${timeStr}_Dr${doctorNameClean}_${clinicNameClean}.pdf`;
    } else {
      return `Invoice_${data?.invoice_number || data?.id}_${patientNameClean}_${dateStr}_Dr${doctorNameClean}_${clinicNameClean}.pdf`;
    }
  };

  // Handle print
  const handlePrint = async () => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    try {
      const originalTitle = document.title;
      const printTitle = generatePrintTitle();
      document.title = printTitle;
      
      if (globalThis.electronAPI) {
        try {
          const result = await globalThis.electronAPI.printPrescription({
            paperSize: 'A4',
            margins: { top: 10, bottom: 10, left: 10, right: 10 },
            silent: false,
            scale: 100,
            jobName: printTitle
          });
          
          if (result.success) {
            toast({
              title: "Print Successful",
              description: `${type === 'prescription' ? 'Prescription' : 'Invoice'} sent to printer successfully`,
              variant: "default"
            });
          } else {
            throw new Error(result.error || 'Electron print failed');
          }
        } catch {
          // Fallback to browser print
          globalThis.print();
        }
      } else {
        globalThis.print();
        toast({
          title: "Print Dialog Opened",
          description: "Please select your printer and confirm print settings",
          variant: "default"
        });
      }
      
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch {
      toast({
        title: "Print Error",
        description: `Failed to print. Please try again.`,
        variant: "destructive"
      });
    } finally {
      setIsPrinting(false);
    }
  };

  // Handle PDF save
  const handleSavePDF = async () => {
    if (!globalThis.electronAPI) {
      toast({
        title: "Not Available",
        description: "PDF save is only available in the desktop app",
        variant: "destructive"
      });
      return;
    }

    if (isSavingPDF) return;
    
    setIsSavingPDF(true);
    
    try {
      const filename = generateFilename();
      const printTitle = generatePrintTitle();
      
      const pdfOptions = {
        paperSize: 'A4',
        marginsType: 2,
        save: true,
        filename: filename,
        autoOpen: autoOpenPDF,
        metadata: {
          title: printTitle,
          author: `Dr. ${settings?.profile?.name || doctorInfo?.name || 'Doctor'}`,
          subject: `Medical ${type === 'prescription' ? 'Prescription' : 'Invoice'} dated ${data?.date?.split('T')[0] || new Date().toISOString().split('T')[0]}`,
          creator: `${settings?.clinic?.name || clinicInfo?.name || 'Medical Clinic'} - Hospital Management System`,
          keywords: `${type}, medical, ${patientInfo?.name || data?.patient_name || 'patient'}, ${settings?.profile?.name || doctorInfo?.name || 'doctor'}`,
          creationDate: (data?.date ? new Date(data.date) : new Date()).toISOString(),
          modDate: new Date().toISOString()
        }
      };
      
      console.log("Calling electronAPI.printToPDF with options:", pdfOptions);
      
      if (!globalThis.electronAPI?.printToPDF) {
        throw new Error('PDF functionality not available. Please ensure you are running in Electron.');
      }
      
      const result = await globalThis.electronAPI.printToPDF(pdfOptions);
      console.log("PDF save result:", result);
      
      if (result.success) {
        const openedText = result.opened ? " and opened" : "";
        toast({
          title: "PDF Saved Successfully",
          description: result.path ? `Saved${openedText}: ${result.path}` : `PDF has been saved${openedText}`,
          variant: "default"
        });
      } else {
        console.error("PDF save failed:", result);
        throw new Error(result.error || 'Failed to save PDF');
      }
    } catch (error) {
      console.error("PDF save error:", error);
      toast({
        title: "PDF Export Error",
        description: "Failed to save PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSavingPDF(false);
    }
  };

  // Create print styles
  useEffect(() => {
    const printStyleId = 'unified-print-styles';
    const existingStyle = document.getElementById(printStyleId);
    
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = printStyleId;
      style.textContent = `
            @page {
              size: A4;
              margin: 0;
            }
          
            @media print {
              html, body {
                width: 210mm;
                height: 297mm;
                margin: 0 !important;
                padding: 0 !important;
                overflow: hidden !important;
              }
          
              body * {
                visibility: hidden !important;
              }
          
              .unified-print-area,
              .unified-print-area * {
                visibility: visible !important;
              }
          
              .unified-print-area {
                position: fixed !important;
                left: 0 !important;
                top: 0 !important;
                width: 210mm !important;
                min-height: 297mm !important;
                margin: 0 !important;
                padding: 10mm !important;
                box-sizing: border-box !important;
                box-shadow: none !important;
                border: none !important;
                transform: none !important;
                max-width: none !important;
                background: white !important;
                overflow: hidden !important;
              }
          
              .print-controls {
                display: none !important;
              }
            }
          
            @media screen and (max-width: 1200px) {
              .unified-print-area {
                font-size: 10px !important;
              }
            }
          
            @media screen and (max-width: 1000px) {
              .unified-print-area {
                font-size: 9px !important;
              }
            }
          `;
      document.head.appendChild(style);
    }

    return () => {
      const styleElement = document.getElementById(printStyleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-[96vw] max-h-[96vh] w-full h-full flex p-0" showClose={false}>
        
        {/* Print Preview Area - Left Side */}
                <div className="flex-1 bg-muted/20 border-r border-border overflow-auto">
          <div className="bg-gray-100 px-4 py-2 border-b print-controls">
            <h3 className="font-medium text-gray-700">
              {title || `${type === 'prescription' ? 'Prescription' : 'Invoice'} Print Preview (A4 - 210×297mm)`}
            </h3>
          </div>
          
          <div className="flex justify-center items-start py-4 px-2 min-h-full">
            {isLoading ? (
              <div className="flex items-center justify-center w-full h-64">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-sm text-muted-foreground">Loading preview...</p>
                </div>
              </div>
            ) : (
              <div 
                className="unified-print-area bg-background shadow-lg border border-border" 
                style={{
                  width: 'min(210mm, calc(100vw - 400px))',
                  minHeight: '297mm',
                  maxWidth: '794px',
                  padding: '15mm',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '11px',
                  lineHeight: '1.2',
                  color: '#000',
                  transformOrigin: 'top center'
                }}
              >
                {type === 'prescription' ? (
                  <PrescriptionContent 
                    data={data}
                    translations={t}
                    language={printLanguage}
                    clinicInfo={settings?.clinic || clinicInfo}
                    doctorInfo={settings?.profile || doctorInfo}
                    patientInfo={patientInfo}
                    settings={settings}
                    currentUser={currentUser}
                  />
                ) : (
                  <InvoiceContent 
                    data={data}
                    translations={t}
                    language={printLanguage}
                    clinicInfo={settings?.clinic || clinicInfo}
                    doctorInfo={settings?.profile || doctorInfo}
                    patientInfo={patientInfo}
                    settings={settings}
                    currentUser={currentUser}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Controls Panel - Right Side */}
                <div className="w-80 max-w-[25vw] bg-muted/20 p-4 space-y-4 print-controls overflow-y-auto">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 text-sm">Print Settings</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Document Summary */}
          <div className="space-y-2 p-3 bg-background rounded-lg border border-border">
            <div className="flex items-center gap-2 text-sm font-medium">
              {type === 'prescription' ? (
                <FileText className="w-4 h-4 text-primary" />
              ) : (
                <IndianRupee className="w-4 h-4 text-primary" />
              )}
              {type === 'prescription' ? 'Prescription Summary' : 'Invoice Summary'}
            </div>
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span>{type === 'prescription' ? 'Patient:' : 'Patient:'}</span>
                <span className="font-medium capitalize">{patientInfo?.name || data?.patient_name}</span>
              </div>
              {type === 'invoice' && (
                <>
                  <div className="flex justify-between">
                    <span>Invoice:</span>
                    <span className="font-medium">{data?.invoice_number || data?.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span className="font-medium">₹{Number(data?.total_amount || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span>Date:</span>
                <span className="font-medium">{data?.date ? DateUtil.formatDateForType(data.date, 'date-time') : 'Today'}</span>
              </div>
            </div>
          </div>
          
          {/* Print Language */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Languages className="w-4 h-4" />
              Print Language
            </Label>
            <Select value={printLanguage} onValueChange={(value) => setPrintLanguage(value as PrintLanguage)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">हिंदी (Hindi)</SelectItem>
                <SelectItem value="mr">मराठी (Marathi)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              type="email"
              placeholder="Enter Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9"
            />
          </div>
          
          {/* Phone Number */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone Number
            </Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-9"
            />
          </div>
          
          {/* PDF Settings - Only show for Electron */}
          {globalThis.electronAPI && (
            <div className="space-y-2 p-3 bg-background rounded-lg border border-border">
              <div className="text-sm font-medium text-gray-900 mb-2">PDF Options</div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="autoOpen"
                  checked={autoOpenPDF}
                  onCheckedChange={(checked) => setAutoOpenPDF(checked === true)}
                />
                <Label
                  htmlFor="autoOpen"
                  className="text-sm font-normal cursor-pointer"
                >
                  Auto-open PDF after saving
                </Label>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <Button 
              onClick={handlePrint}
              disabled={isPrinting}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isPrinting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Printing...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" />
                  Print {type === 'prescription' ? 'Prescription' : 'Invoice'}
                </>
              )}
            </Button>

            {globalThis.electronAPI && (
              <Button
                onClick={handleSavePDF}
                disabled={isSavingPDF}
                variant="outline"
                className="w-full text-primary border-primary hover:bg-primary/10"
              >
                {isSavingPDF ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving PDF...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Save as PDF
                  </>
                )}
              </Button>
            )}
            
            <Button
              variant="outline"
              className="w-full text-info border-info/20 hover:bg-info/10 hidden"
              disabled={!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
              onClick={() => {
                toast({
                  title: "Email Feature",
                  description: "Email functionality will be implemented soon",
                  variant: "default"
                });
              }}
            >
              <Mail className="w-4 h-4 mr-2" />
              Email {type === 'prescription' ? 'Prescription' : 'Invoice'}
            </Button>

            {/* Cancel Button */}
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full text-muted-foreground border-muted-foreground/20 hover:bg-muted-foreground/10"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 text-center pt-2">
            {printLanguage === 'mr' ? 'मराठी' : printLanguage === 'hi' ? 'हिंदी' : 'English'} format
          </div>
        </div>
        
      </DialogContent>
    </Dialog>
    </ErrorBoundary>
  );
}

export default memo(UnifiedPrintModal); 